/**
 * "My ideas" — the signed-in dashboard.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Idea, IdeasResponse } from '@ideabrief/core';
import { ApiError, theme } from '@ideabrief/core';
import { api, authClient } from '../auth';
import { hasDraft } from '../lib/draft';
import { ResendVerification } from '../components/ResendVerification';
import { Button, Logo, Mono, Notice, Wordmark } from '../components/ui';

interface IdeasProps {
  user: { name: string | null; email: string; emailVerified: boolean };
}

type FilterKey = 'all' | 'waiting' | 'research' | 'reports';

const FILTERS: readonly { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'waiting', label: 'Waiting on you' },
  { key: 'research', label: 'In research' },
  { key: 'reports', label: 'Reports' },
];

/** Which statuses each filter pill admits. */
const FILTER_STATUSES: Record<FilterKey, readonly Idea['status'][]> = {
  all: ['draft', 'awaiting_refinement', 'in_research', 'report_ready'],
  waiting: ['draft', 'awaiting_refinement'],
  research: ['in_research'],
  reports: ['report_ready'],
};

/** Verdict chip beside the status badge, once a report has landed. */
const VERDICT_LABELS: Record<string, { label: string; tone: 'green' | 'amber' }> = {
  proceed: { label: 'PROCEED', tone: 'green' },
  proceed_with_changes: { label: 'PROCEED WITH CHANGES', tone: 'amber' },
  rethink: { label: 'NEEDS A RETHINK', tone: 'amber' },
};

/** "12 JUL" — short, uppercase, no year. */
function shortDate(iso: string): string {
  return new Date(iso)
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    .toUpperCase();
}

/**
 * The card's mono meta line.
 *
 * Says the thing that is true of THIS status rather than one generic
 * timestamp: a waiting idea's useful fact is how many questions are left, an
 * in-research idea's is when the report is due. All of it is derived from real
 * columns — nothing here is invented to fill the line.
 */
function cardMeta(idea: Idea): string {
  const { answered, total } = idea.refinement;

  switch (idea.status) {
    case 'report_ready':
      return `REPORTED ${shortDate(idea.updatedAt)}`;
    case 'in_research': {
      // The 48h promise, counted from submission (updated_at at submit time).
      const due = new Date(new Date(idea.updatedAt).getTime() + 48 * 3600 * 1000);
      return `SUBMITTED ${shortDate(idea.updatedAt)} · REPORT BY ${shortDate(due.toISOString())}`;
    }
    case 'awaiting_refinement':
      return total > 0
        ? `SCANNED ${shortDate(idea.updatedAt)} · ${answered} OF ${total} QUESTIONS ANSWERED`
        : `SCANNED ${shortDate(idea.updatedAt)}`;
    default:
      return `STARTED ${shortDate(idea.createdAt)} · INTERVIEW UNFINISHED`;
  }
}

export function Ideas({ user }: IdeasProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<IdeasResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  // Read once on mount: the draft only changes on other screens, and re-reading
  // localStorage on every render would flicker the banner during the scan
  // navigation that clears it.
  const [draftWaiting] = useState(hasDraft);
  const [toast, setToast] = useState<string | null>(null);
  /** Id awaiting delete confirmation — the card asks in place, not in a modal. */
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  /**
   * Deleting removes the idea from the local list immediately rather than
   * refetching: the server is the authority, but a full reload of the
   * dashboard to reflect one removal is a visible stutter for no new
   * information. A failure puts the row back by refetching.
   */
  async function handleDelete(id: string) {
    setConfirmDelete(null);
    const previous = data;
    setData((d) =>
      d
        ? {
            ...d,
            groups: d.groups
              .map((g) => ({ ...g, ideas: g.ideas.filter((i) => i.id !== id) }))
              .filter((g) => g.ideas.length > 0),
            total: Math.max(0, d.total - 1),
          }
        : d,
    );

    const res = await fetch(`/api/ideas/${id}`, { method: 'DELETE' }).catch(
      () => null,
    );

    if (!res || (!res.ok && res.status !== 404)) {
      // Put it back — the row still exists server-side.
      setData(previous);
      setToast("We couldn't delete that idea. Try again.");
      return;
    }
    setToast('Idea deleted — gone within 24h');
  }

  useEffect(() => {
    let active = true;
    api
      .getIdeas()
      .then((res) => active && setData(res))
      .catch((err: unknown) => {
        if (!active) return;
        setError(
          err instanceof ApiError
            ? err.message
            : "We couldn't load your ideas. Refresh to try again.",
        );
      });
    return () => {
      active = false;
    };
  }, []);

  async function handleSignOut() {
    await authClient.signOut();
    navigate('/', { replace: true });
  }

  const firstName = user.name?.split(' ')[0] ?? 'there';
  const initial = (user.name ?? user.email).charAt(0).toUpperCase();

  // Search and filter run client-side over the already-loaded list: the whole
  // set is small, so a server round-trip would add latency for nothing.
  const needle = query.trim().toLowerCase();
  const allowed = FILTER_STATUSES[filter];
  const visibleGroups = (data?.groups ?? [])
    .map((group) => ({
      ...group,
      ideas: group.ideas.filter(
        (idea) =>
          allowed.includes(idea.status) &&
          (!needle ||
            idea.title.toLowerCase().includes(needle) ||
            (idea.summary ?? '').toLowerCase().includes(needle)),
      ),
    }))
    .filter((group) => group.ideas.length > 0);

  return (
    <div style={{ minHeight: '100vh', animation: 'ibFadeIn .3s ease both' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: 'rgba(251,250,248,.9)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid var(--line2)',
        }}
      >
        <div
          style={{
            maxWidth: 960,
            margin: '0 auto',
            padding: '13px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto' }}
          >
            <Logo size={18} />
            <Wordmark size={14.5} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span
              aria-hidden="true"
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'var(--acc)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12.5,
                fontWeight: 600,
                flex: 'none',
              }}
            >
              {initial}
            </span>
            <span style={{ fontSize: 13.5, color: 'var(--ink2)' }}>{firstName}</span>
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '38px 20px 100px' }}>
        {!user.emailVerified && (
          <div style={{ marginBottom: 26 }}>
            {/* Wording matters: verification does NOT gate evaluating ideas —
                only report DELIVERY (SECURITY.md §2, scan.ts header). The old
                copy ("Confirm your email to start evaluating ideas") described
                a wall that does not exist and told users to stop when they
                could carry straight on. */}
            <Notice tone="amber">
              <strong>Confirm your email to get your reports.</strong> We sent a
              link to {user.email}. You can keep evaluating ideas meanwhile —
              confirming is what lets us deliver the finished report.
              <ResendVerification email={user.email} />
            </Notice>
          </div>
        )}

        {/*
          Safety net for an unfinished interview.

          The flow normally returns a user straight to /launch after auth, but
          anyone who lands here with answers still in localStorage (signed in on
          another tab, hit Back, closed the tab mid-scan) would otherwise see no
          trace of them — and "Evaluate a new idea" would start again from
          question 1, silently abandoning eight answers.
        */}
        {draftWaiting && (
          <div style={{ marginBottom: 20 }}>
            <Notice tone="accent">
              <strong style={{ display: 'block', marginBottom: 4 }}>
                You have an unfinished idea.
              </strong>
              Your answers are saved on this device. Pick up where you left off
              and run the scan.
              <div style={{ marginTop: 12 }}>
                <Button size="sm" onClick={() => navigate('/launch')}>
                  Finish your brief
                </Button>
              </div>
            </Notice>
          </div>
        )}

        <div
          style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}
        >
          <div style={{ marginRight: 'auto' }}>
            <div style={{ marginBottom: 12 }}>
              <Mono size={11}>{data?.triage ?? ' '}</Mono>
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: 34,
                lineHeight: 1.14,
                letterSpacing: '-.03em',
                fontWeight: 600,
              }}
            >
              Your ideas
            </h1>
          </div>
          {/* Verification deliberately does NOT gate this. It is silent and
              non-blocking through interview and scan; only report delivery is
              gated (SECURITY.md §2, BACKLOG.md decision 4). */}
          <Button onClick={() => navigate('/capture')}>
            Evaluate a new idea
          </Button>
        </div>

        {/* Search + filters. Client-side over title, description and status —
            there is no server round-trip and no pagination to keep in sync. */}
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 22,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 220,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'var(--card)',
              border: '1px solid var(--line)',
              borderRadius: 10,
              padding: '0 14px',
              height: 44,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.5" stroke="var(--muted)" strokeWidth="1.4" />
              <path d="M10.5 10.5L14 14" stroke="var(--muted)" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your ideas"
              aria-label="Search your ideas"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 16,
                fontFamily: 'inherit',
                color: 'var(--ink)',
              }}
            />
          </div>

          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                aria-pressed={active}
                style={{
                  border: `1px solid ${active ? 'var(--ink)' : 'var(--line)'}`,
                  borderRadius: 20,
                  padding: '10px 14px',
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  background: active ? 'var(--ink)' : 'var(--card)',
                  color: active ? '#fff' : 'var(--ink2)',
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {error && (
          <div style={{ marginTop: 26 }}>
            <Notice tone="amber">{error}</Notice>
          </div>
        )}

        {!data && !error && (
          <p style={{ marginTop: 26, fontSize: 14.5, color: 'var(--muted)' }}>
            Loading your ideas…
          </p>
        )}

        {data && data.total === 0 && <EmptyState verified={user.emailVerified} />}

        {/* Empty groups collapse rather than rendering a bare header. */}
        {visibleGroups.map((group) => (
          <section key={group.key} style={{ marginTop: 26 }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: groupDot(group.key),
                }}
              />
              <Mono color={groupDot(group.key)}>{group.label}</Mono>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {group.ideas.map((idea) => (
                <IdeaCard
                  key={idea.id}
                  idea={idea}
                  confirming={confirmDelete === idea.id}
                  onAskDelete={() => setConfirmDelete(idea.id)}
                  onCancelDelete={() => setConfirmDelete(null)}
                  onConfirmDelete={() => void handleDelete(idea.id)}
                />
              ))}
            </div>
          </section>
        ))}

        {data && data.total > 0 && visibleGroups.length === 0 && (
          <div
            style={{
              marginTop: 26,
              border: '1px dashed var(--line)',
              borderRadius: 16,
              padding: '40px 24px',
              textAlign: 'center',
              background: 'var(--card)',
            }}
          >
            <p style={{ margin: 0, fontSize: 15, color: 'var(--muted)' }}>
              No ideas match that search.
            </p>
            <div style={{ marginTop: 14 }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setQuery('');
                  setFilter('all');
                }}
              >
                Clear search &amp; filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--ink)',
            color: '#fff',
            borderRadius: 20,
            padding: '10px 18px',
            fontSize: 13.5,
            zIndex: 20,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

function groupDot(key: string): string {
  if (key === 'just_landed') return 'var(--green)';
  if (key === 'waiting_on_you') return 'var(--amber)';
  return 'var(--acc)';
}

function IdeaCard({
  idea,
  confirming,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
}: {
  idea: Idea;
  confirming: boolean;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}) {
  const navigate = useNavigate();
  const tokens = theme.statusTokens[idea.status];
  const label = theme.statusLabels[idea.status];
  const verdict = idea.verdict ? VERDICT_LABELS[idea.verdict] : null;

  return (
    <article
      style={{
        background: 'var(--card)',
        border: '1px solid var(--line)',
        borderRadius: 16,
        padding: '20px 22px',
        boxShadow: 'var(--shadow-card)',
      }}
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              flexWrap: 'wrap',
              marginBottom: 8,
            }}
          >
            {idea.isNew && (
              <span
                style={{
                  font: "600 10px/1 var(--font-mono)",
                  letterSpacing: '.06em',
                  color: '#fff',
                  background: 'var(--acc)',
                  borderRadius: 5,
                  padding: '4px 7px 3px',
                }}
              >
                NEW
              </span>
            )}
            <span
              style={{
                font: "500 10px/1 var(--font-mono)",
                letterSpacing: '.05em',
                color: tokens.fg,
                background: tokens.bg,
                border: `1px solid ${tokens.line}`,
                borderRadius: 5,
                padding: '4px 7px 3px',
              }}
            >
              {label}
            </span>

            {/* The call itself, beside the status. REPORT READY says a report
                exists; this says what it concluded — the thing worth scanning
                a list for. */}
            {verdict && (
              <span
                style={{
                  font: "600 10px/1 var(--font-mono)",
                  letterSpacing: '.05em',
                  color: `var(--${verdict.tone})`,
                  background: `var(--${verdict.tone}-soft)`,
                  border: `1px solid var(--${verdict.tone}-line)`,
                  borderRadius: 5,
                  padding: '4px 7px 3px',
                }}
              >
                {verdict.label}
              </span>
            )}

            <Mono size={11}>{cardMeta(idea)}</Mono>
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-.018em',
              lineHeight: 1.35,
            }}
          >
            {idea.title}
          </h2>
          {idea.summary && (
            <p
              style={{
                margin: '5px 0 0',
                fontSize: 13.5,
                color: 'var(--muted)',
                lineHeight: 1.5,
              }}
            >
              {idea.summary}
            </p>
          )}

          {/* Live ticker: the one card state that is actively changing. Named
              stages rather than a source count — there is no research pipeline
              reporting numbers yet, and a fabricated counter on the card that
              says "trust the report" would be exactly the wrong invention. */}
          {idea.status === 'in_research' && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 9,
                marginTop: 12,
                background: 'var(--green-soft)',
                border: '1px solid var(--green-line)',
                borderRadius: 8,
                padding: '8px 12px',
                flexWrap: 'wrap',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--acc)',
                  animation: 'ibPulse 1.8s ease-out infinite',
                  flex: 'none',
                }}
              />
              <Mono size={10.5} color="var(--ink2)">
                DEEP RESEARCH RUNNING · MARKET SIZING, COMPETITORS, TIMING, RISKS
              </Mono>
            </div>
          )}
        </div>

        {idea.score !== null && (
          <div style={{ flex: 'none', textAlign: 'right' }}>
            <div
              style={{
                fontSize: 26,
                fontWeight: 600,
                letterSpacing: '-.03em',
                lineHeight: 1,
                color: 'var(--acc-ink)',
              }}
            >
              {idea.score.toFixed(1)}
            </div>
            {idea.percentile && (
              <div style={{ marginTop: 6 }}>
                <Mono size={10}>{idea.percentile}</Mono>
              </div>
            )}
          </div>
        )}

        {/* Delete asks in place rather than in a modal — a confirm dialog for
            one row is heavier than the action. Deliberately low-contrast until
            hovered/asked: it is destructive and permanent, so it should never
            compete with the primary action for a mis-click. */}
        <div style={{ flex: 'none' }}>
          {confirming ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Mono size={10}>DELETE?</Mono>
              <button
                onClick={onConfirmDelete}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '2px 0',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--error)',
                  cursor: 'pointer',
                }}
              >
                Yes, delete
              </button>
              <button
                onClick={onCancelDelete}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '2px 0',
                  fontSize: 12,
                  color: 'var(--muted)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={onAskDelete}
              title="Delete this idea"
              style={{
                background: 'none',
                border: 'none',
                padding: '2px 0',
                fontSize: 12,
                color: 'var(--muted)',
                cursor: 'pointer',
                opacity: 0.55,
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Phase-matched actions, primary first. A draft has no scan yet, so it
          resumes the interview rather than opening a dashboard. */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid var(--line2)',
        }}
      >
        {idea.status === 'draft' ? (
          <Button size="sm" onClick={() => navigate('/capture')}>
            Resume interview
          </Button>
        ) : (
          <Button size="sm" onClick={() => navigate(`/idea/${idea.id}`)}>
            {idea.status === 'awaiting_refinement'
              ? 'Finish refinement'
              : idea.status === 'report_ready'
                ? 'Open report'
                : 'Open scan dashboard'}
          </Button>
        )}

        {idea.reports.map((r) => (
          <Button
            key={r.id}
            size="sm"
            variant="secondary"
            onClick={() => {
              window.location.href = `/api/reports/${r.id}`;
            }}
          >
            {r.kind === 'deck'
              ? 'Executive deck (PDF)'
              : r.kind === 'detailed'
                ? 'Detailed report (PDF)'
                : 'Verdict page'}
          </Button>
        ))}
      </div>
    </article>
  );
}

function EmptyState({ verified }: { verified: boolean }) {
  return (
    <div
      style={{
        marginTop: 28,
        border: '1px dashed var(--line)',
        borderRadius: 16,
        padding: '56px 24px',
        textAlign: 'center',
        background: 'var(--card)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          borderRadius: 11,
          background: 'var(--acc-soft)',
          border: '1px solid var(--acc-line)',
          margin: '0 auto 18px',
        }}
      />
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: '-.02em' }}>
        No ideas yet
      </h2>
      <p
        style={{
          margin: '10px auto 20px',
          fontSize: 14.5,
          lineHeight: 1.6,
          color: 'var(--muted)',
          maxWidth: '44ch',
        }}
      >
        Six minutes of questions and you'll have an instant scan — and a full
        research report within 48 hours.
      </p>
      <Button disabled={!verified}>Evaluate my first idea</Button>
    </div>
  );
}
